/**
 * Development logging utility with log levels and structured output
 */

type LogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

const isDevelopment = import.meta.env.DEV;
const logLevel = (import.meta.env.VITE_LOG_LEVEL || "INFO").toUpperCase() as LogLevel;

function shouldLog(level: LogLevel): boolean {
  if (!isDevelopment) return level === "ERROR";
  return LOG_LEVELS[level] >= LOG_LEVELS[logLevel];
}

function formatMessage(level: LogLevel, message: string, ...args: unknown[]): string {
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${level}]`;
  return `${prefix} ${message}`;
}

export const logger = {
  debug(message: string, ...args: unknown[]): void {
    if (shouldLog("DEBUG")) {
      console.debug(formatMessage("DEBUG", message), ...args);
    }
  },

  info(message: string, ...args: unknown[]): void {
    if (shouldLog("INFO")) {
      console.info(formatMessage("INFO", message), ...args);
    }
  },

  warn(message: string, ...args: unknown[]): void {
    if (shouldLog("WARN")) {
      console.warn(formatMessage("WARN", message), ...args);
    }
  },

  error(message: string, error?: Error | unknown, ...args: unknown[]): void {
    if (shouldLog("ERROR")) {
      const errorDetails = error instanceof Error 
        ? { message: error.message, stack: error.stack }
        : error;
      console.error(formatMessage("ERROR", message), errorDetails, ...args);
    }
  },

  // Convex-specific logging helpers
  convex: {
    query(functionName: string, args?: unknown): void {
      logger.debug(`[Convex Query] ${functionName}`, args);
    },
    mutation(functionName: string, args?: unknown): void {
      logger.debug(`[Convex Mutation] ${functionName}`, args);
    },
    action(functionName: string, args?: unknown): void {
      logger.debug(`[Convex Action] ${functionName}`, args);
    },
    response(functionName: string, result: unknown): void {
      logger.debug(`[Convex Response] ${functionName}`, result);
    },
    error(functionName: string, error: Error | unknown): void {
      logger.error(`[Convex Error] ${functionName}`, error);
    },
  },
};

// Expose logger to window for browser console access
if (isDevelopment && typeof window !== "undefined") {
  (window as unknown as { logger: typeof logger }).logger = logger;
}

